import React, { useMemo, useState, useEffect, forwardRef,
  useImperativeHandle } from "react";
import { Trans, useTranslation } from 'react-i18next';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import {
  IDENTIFICATION_NUMBER_MAX_LENGTH,
  isIdentificationNumberContentValid,
  sanitizeIdentificationNumberInput,
} from "../../../../../helper/identificationNumberValidation";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export interface ReceiverIdentificationNumberStepHandle {
  submit: () => Promise<boolean>;
}

const ReceiverIdentificationNumberStep = forwardRef<ReceiverIdentificationNumberStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t, i18n } = useTranslation();
  const [errors, setErrors] = useState({
          idNumber: "",
    });
  const storedReceiverPhotoId = useSessionStore((s: SessionState) => (s as any).receiverPhotoIdNumber as string | null | undefined);
  const [idNumber, setIdNumber] = useState(() => {
    const raw = (useSessionStore.getState() as any).receiverPhotoIdNumber as string | null | undefined;
    const cleaned = sanitizeIdentificationNumberInput(String(raw ?? ""));
    return cleaned.toUpperCase();
  });
  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey);
  const selectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).selectedIdTypeLabel);
  const setReceiverPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).setReceiverPhotoIdNumber);

  useEffect(() => {
    const cleaned = sanitizeIdentificationNumberInput(String(storedReceiverPhotoId ?? ""));
    setIdNumber(cleaned.toUpperCase());
  }, [storedReceiverPhotoId]);

  const placeholderText = useMemo(() => {
    const baseLabel = selectedIdTypeLabel || t("US_DRIVERS_LICENSE_NUMBER");
    return `${baseLabel} ${t("IDENTIFICATION_NUMBER_SUFFIX")}`;
  }, [selectedIdTypeLabel, t]);

  const isValid =
    idNumber.trim().length > 0 && isIdentificationNumberContentValid(idNumber);
      
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const trimmed = idNumber.trim();
        const newErrors = {
          idNumber: !trimmed
            ? t("ERROR_IDENTIFICATION_NUMBER_IS_REQUIRED")
            : trimmed.length > IDENTIFICATION_NUMBER_MAX_LENGTH
              ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: IDENTIFICATION_NUMBER_MAX_LENGTH })
              : !isIdentificationNumberContentValid(idNumber)
                ? t("ID_NUMBER_INVALID_CHARS")
                : "",
        };
        setErrors(newErrors);
      
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));

  const handleManualInput = async (text: string) => {
    const cleaned = sanitizeIdentificationNumberInput(text);
    const upper = cleaned.toUpperCase();
    setIdNumber(upper);
    try { setReceiverPhotoIdNumber?.(upper); } catch {}
    if (errors.idNumber) setErrors({ ...errors, idNumber: "" });
  };

  const formatDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerWrap}>
        <Text style={styles.title}>
          <Trans i18nKey="IDENTIFICATION_NUMBER_TITLE" />
        </Text>
        <Text style={styles.subtitle}>{t("PROVIDE_YOUR_PHOTO_ID_NUMBER")}</Text>
      </View>
      <FloatingTextInputComponent
        testID="receiverIdentificationNumber"
        isAutoFocus={true}
        label={placeholderText}
        value={idNumber}
        onChangeText={handleManualInput}
        errorText={errors.idNumber}
        onClearError={() => setErrors((prev) => ({ ...prev, idNumber: "" }))}
        autoCapitalize="characters"
        maxLength={IDENTIFICATION_NUMBER_MAX_LENGTH}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    // paddingBottom: 200
  },
  headerWrap: {
    flexShrink: 0,
    ...(Platform.OS === 'web' && {
      position: 'sticky' as any,
      top: 0,
      zIndex: 10,
      backgroundColor: Colors.white,
      paddingBottom: 8,
    }),
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
    // paddingRight: 100
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
});

export default ReceiverIdentificationNumberStep;